import React from 'react';
import { useQuery } from 'react-apollo';
import { t, defineMessages } from '@sm/intl';
import { createURL } from '@sm/utils';
import { TopBarSlim } from '../../TopBarSlim';
import { UseCaseVariationEnums } from '../../TopBarSlimEnums';
import useCaseStudentCommunityFeedbackTopBarSlimQuery from './UseCaseStudentCommunityFeedbackTopBarSlim.graphql';
import TopBarSlimSkeleton from '../../TopBarSlimSkeleton';
import { EmptyComponent } from '../../../helpers/EmptyComponent';

const DEFAULT_COPY = defineMessages({
  MESSAGE: {
    id: 'UseCaseStudentCommunityFeedbackTopBarSlim.message',
    defaultMessage:
      'Use Quizzes Pro to give recipients feedback based on the accuracy of their answers.',
    description: '[Type: Paragraph][Vis.: low] - Top bar slim message',
  },
  CTA_TEXT: {
    id: 'UseCaseStudentCommunityFeedbackTopBarSlim.ctaText',
    defaultMessage: 'See all plans',
    description: '[Type: Button][Vis.: low] - Top bar slim cta text',
  },
});

const COLLECT_COPY = defineMessages({
  MESSAGE: {
    id: 'UseCaseStudentCommunityFeedbackTopBarSlim.messageCollect',
    defaultMessage:
      'Give everyone a voice. Get a plan that unlocks access to over { analyzeResponseLimit } survey responses.',
    description: '[Type: Paragraph][Vis.: low] - Top bar slim message',
  },
  CTA_TEXT: {
    id: 'UseCaseStudentCommunityFeedbackTopBarSlim.ctaTextCollect',
    defaultMessage: 'See all plans',
    description: '[Type: Button][Vis.: low] - Top bar slim cta text',
  },
});

const ANALYZE_COPY = defineMessages({
  MESSAGE: {
    id: 'UseCaseStudentCommunityFeedbackTopBarSlim.messageAnalyze',
    defaultMessage:
      'Share survey feedback. Export and print results with upgraded plans.',
    description: '[Type: Paragraph][Vis.: low] - Top bar slim message',
  },
  CTA_TEXT: {
    id: 'UseCaseStudentCommunityFeedbackTopBarSlim.ctaTextAnalyze',
    defaultMessage: 'See all plans',
    description: '[Type: Button][Vis.: low] - Top bar slim cta text',
  },
});

export type UseCaseStudentCommunityFeedbackTopBarSlimProps = {
  showLoaderOnFetch?: boolean;
  variation?: UseCaseVariationEnums | null;
};

const VariationToCopy = {
  [UseCaseVariationEnums.create]: DEFAULT_COPY,
  [UseCaseVariationEnums.collect]: COLLECT_COPY,
  [UseCaseVariationEnums.analyze]: ANALYZE_COPY,
};

export const UseCaseStudentCommunityFeedbackTopBarSlim: React.FC<UseCaseStudentCommunityFeedbackTopBarSlimProps> = ({
  showLoaderOnFetch,
  variation,
}) => {
  const LoaderComponent: React.FC = showLoaderOnFetch
    ? TopBarSlimSkeleton
    : EmptyComponent;

  // Fetch data required for component
  const { loading, error, data } = useQuery(
    useCaseStudentCommunityFeedbackTopBarSlimQuery
  );
  const analyzeResponseLimit = data?.user?.limits?.analyzeResponseLimit;

  const copy =
    variation && variation in VariationToCopy
      ? VariationToCopy[variation]
      : DEFAULT_COPY;
  const ut_source = variation
    ? `slim_${variation}_upsell_student_community_feedback`
    : 'slim_create_upsell_student_community_feedback';

  return (
    <>
      {loading ? (
        <LoaderComponent />
      ) : error || !analyzeResponseLimit ? (
        <EmptyComponent />
      ) : (
        <TopBarSlim
          message={t(copy.MESSAGE, { analyzeResponseLimit })}
          ctaText={t(copy.CTA_TEXT)}
          ctaLink={createURL('/pricing/upgrade/', {
            ut_source: 'topbar',
            ut_source2: ut_source,
          })}
          ctaNewTab
        />
      )}
    </>
  );
};

export default UseCaseStudentCommunityFeedbackTopBarSlim;
